// RevealStagger.js — Revela uma LISTA de filhos em cascata (um após o outro).
// Usado nas grades de capas: os cards entram em sequência curta, o que dá
// ritmo de leitura da esquerda para a direita sem atrasar nada de verdade.
//
// Como o Reveal, a animação é CSS e o estado final não depende do relógio de
// animação (ver useRevelar.js). É a grade do catálogo que mais se beneficia
// disso: são 12 capas que precisam estar na tela mesmo que nada anime.
//
// Uso:
//   <RevealStagger className="grid ...">
//     {livros.map(l => <RevealStagger.Item key={l.id}><Card .../></RevealStagger.Item>)}
//   </RevealStagger>
import React, { useRef } from "react";
import { useReducedMotion } from "motion/react";
import useRevelar, { classesDoEstado } from "./useRevelar";

// Teto do atraso: numa grade de 12+ capas, 0,05 s por item acumulado faria o
// último card entrar meio segundo depois do primeiro. Passando disso, o
// restante entra junto.
const ATRASO_POR_ITEM = 0.05;
const ATRASO_MAXIMO = 0.4;

const RevealStagger = ({ children, className = "", as: Tag = "div", amount = 0.05, ...rest }) => {
  const ref = useRef(null);
  const estado = useRevelar(ref, amount);
  const reduzirMovimento = useReducedMotion();

  if (reduzirMovimento) {
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  return (
    <Tag ref={ref} className={className} {...rest}>
      {React.Children.map(children, (filho, i) =>
        React.isValidElement(filho)
          ? React.cloneElement(filho, { estado, indice: i })
          : filho
      )}
    </Tag>
  );
};

// `revelado` e `indice` chegam do contêiner via cloneElement. Os padrões
// deixam o Item utilizável sozinho, sem risco de ficar invisível por engano.
const Item = ({ children, className = "", estado = "pronto", indice = 0, style, ...rest }) => {
  const reduzirMovimento = useReducedMotion();

  if (reduzirMovimento) {
    return <div className={className} style={style} {...rest}>{children}</div>;
  }

  return (
    <div
      className={`${classesDoEstado(estado)} ${className}`}
      style={{
        "--revelar-y": "14px",
        transitionDelay: `${Math.min(indice * ATRASO_POR_ITEM, ATRASO_MAXIMO)}s`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
};

RevealStagger.Item = Item;

export default RevealStagger;
